import { Injectable } from '@angular/core';
import { MyPlayerInterface, UserInfoInterface } from 'src/app/core/interfaces';
import { SocketIoService } from 'src/app/core/services';
import { PlayerUtilService } from './player-util.service';
import { UserBackendService } from 'src/app/core/backend-services';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MyPlayerService {
  private initialized = new BehaviorSubject<boolean>(false);
  private _player: MyPlayerInterface;
  private _enemies: number[] = [];
  private playerSource = new BehaviorSubject<MyPlayerInterface | undefined>(
    undefined
  );
  public player$ = this.playerSource.asObservable();

  constructor(
    private socketIO: SocketIoService,
    private playerUtilService: PlayerUtilService,
    private userBackendService: UserBackendService
  ) {}

  public async update(): Promise<void> {
    console.log('STARTING player update');
    const user: any = await this.userBackendService.getPlayerInfo();
    this._enemies = user.enemyUserIDs;
    this._player = MyPlayerInterface.builder()
      .userID(user.userID)
      .username(user.username)
      .avatar3d(user.avatar3d)
      .points(user.points)
      .profilePicture(user.profilePicture)
      .smallProfilePicture(user.smallProfilePicture)
      .tableID(user.tableID)
      .table(user.table)
      .watchingTableID(user.watchingTableID)
      .isInMatchmaking(user.isInMatchmaking || false)
      .wantToLeaveAfterBummerl(user.wantToLeaveAfterBummerl)
      .premiumBadge(user.premiumBadge || undefined)
      .build();
    this.playerSource.next(this._player);
    this.initialized.next(true);
    // Supplementary presence/points must not prevent player initialization.
    const player = this._player;
    void Promise.all([
      this.playerUtilService.updateOnlineStatus(player),
      ...(player.table?.members || []).map(member => this.playerUtilService.updateFromBackend(member)),
    ]).catch(error => console.warn('Player details update failed:', error));
    console.log('FINISHED player update');
  }

  public async updateOnlineStatus() {
    await this.playerUtilService.updateOnlineStatus(this.player);
  }

  public async waitForInit(): Promise<void> {
    await firstValueFrom(
      this.initialized.asObservable().pipe(filter((el) => !!el))
    );
  }

  public get player(): MyPlayerInterface {
    return this._player;
  }

  /**
   * @description returns the enemies (user IDs) of my player
   */
  public get enemies(): number[] {
    return this._enemies;
  }

  public async setMyTable(table: any) {
    if (
      !table ||
      !table.id ||
      !table.members ||
      table.members.filter((el) => el.userID).length === 0
    ) {
      this._player.tableID = 0;
      this._player.table = undefined;
      this.playerSource.next(this._player);
      return;
    }

    this._player.tableID = table.id;
    this._player.table = table;
    this.playerSource.next(this._player);

    await Promise.all(
      table.members.map((player) =>
        this.playerUtilService.updateFromBackend(player)
      )
    );
  }
}
